import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { getISODate } from "../../sdk";
import { useAuth } from "../../sdk/hooks/useAuth";

const safeDivide = (numerator, denominator) => {
  if (
    denominator === 0 ||
    isNaN(denominator) ||
    denominator === null ||
    denominator === undefined 
  ) {
    return 0;
  }
  const result = numerator / denominator;
  return isNaN(result) || !isFinite(result) ? 0 : result;
};

const transformData = (data, fn) => {
  const result = data.map((segment) => ({
    ...segment,
    budgetModel: segment.budgetModel.map((model) => ({
      segmentID: model.segmentID,
      date: model.date,
      roomsActual: fn(model.roomsActual),
      roomsLocked: fn(model.roomsLocked),
      roomsLY: fn(model.roomsLY),
      roomsOTB: fn(model.roomsOTB),
      roomsBudget: fn(model.roomsBudget),
      arrActual: fn(model.arrActual),
      arrLocked: fn(model.arrLocked),
      arrLY: fn(model.arrLY),
      arrOTB: fn(model.arrOTB),
      arrBudget: fn(model.arrBudget),
      revenueActual:
        model.roomsActual > 0 && model.arrActual
          ? fn(model.roomsActual * model.arrActual)
          : -1,
      revenueLocked: fn(model.revenueLocked),
      revenueLY: fn(model.revenueLY),
      revenueOTB: fn(model.revenueOTB),
      revenueBudget: fn(model.revenueBudget),
    })),
  }));

  return result;
};

// Sum all values in a segment's budgetModel, skip total rows (segmentID: 0) because SegmentId 0 is reserved for totals
const calculateBudgetSums = (budgetModel) => {
  return budgetModel.reduce(
    (acc, curr, idx) => {
      const isTotal = curr.segmentID === 0;

      return {
        roomsActual:
          acc.roomsActual +
          (isTotal ? 0 : curr.roomsActual === -1 ? 0 : curr.roomsActual),
        roomsLocked:
          acc.roomsLocked +
          (isTotal ? 0 : curr.roomsLocked === -1 ? 0 : curr.roomsLocked),
        roomsLY:
          acc.roomsLY + (isTotal ? 0 : curr.roomsLY === -1 ? 0 : curr.roomsLY),
        roomsOTB:
          acc.roomsOTB +
          (isTotal ? 0 : curr.roomsOTB === -1 ? 0 : curr.roomsOTB),
        roomsBudget:
          acc.roomsBudget +
          (isTotal ? 0 : curr.roomsBudget === -1 ? 0 : curr.roomsBudget),
        revenueActual:
          acc.revenueActual +
          (isTotal ? 0 : curr.revenueActual === -1 ? 0 : curr.revenueActual),
        revenueLocked:
          acc.revenueLocked +
          (isTotal ? 0 : curr.revenueLocked === -1 ? 0 : curr.revenueLocked),
        revenueLY:
          acc.revenueLY +
          (isTotal ? 0 : curr.revenueLY === -1 ? 0 : curr.revenueLY),
        revenueOTB:
          acc.revenueOTB +
          (isTotal ? 0 : curr.revenueOTB === -1 ? 0 : curr.revenueOTB),
        revenueBudget:
          acc.revenueBudget +
          (isTotal ? 0 : curr.revenueBudget === -1 ? 0 : curr.revenueBudget),
      };
    },
    {
      roomsActual: 0,
      roomsLocked: 0,
      roomsLY: 0,
      roomsOTB: 0,
      roomsBudget: 0,
      revenueActual: 0,
      revenueLocked: 0,
      revenueLY: 0,
      revenueOTB: 0,
      revenueBudget: 0,
    },
  );
};

const calculateADR = (totalRooms, totalRevenue) => {
  return safeDivide(totalRevenue, totalRooms);
};

// Add total row at end of each segment (sum of all dates for that segment)
const addHorizontalTotal = (data) => {
  const result = data.map((segData) => {
    const sums = calculateBudgetSums(segData.budgetModel);

    return {
      ...segData,
      budgetModel: [
        ...segData.budgetModel,
        {
          segmentID: 0,
          date: new Date(),
          roomsActual: sums.roomsActual,
          roomsLocked: sums.roomsLocked,
          roomsLY: sums.roomsLY,
          roomsOTB: sums.roomsOTB,
          roomsBudget: sums.roomsBudget,
          arrActual: calculateADR(sums.roomsActual, sums.revenueActual),
          arrLocked: calculateADR(sums.roomsLocked, sums.revenueLocked),
          arrLY: calculateADR(sums.roomsLY, sums.revenueLY),
          arrOTB: calculateADR(sums.roomsOTB, sums.revenueOTB),
          arrBudget: calculateADR(sums.roomsBudget, sums.revenueBudget),
          revenueActual: sums.revenueActual,
          revenueLocked: sums.revenueLocked,
          revenueLY: sums.revenueLY,
          revenueOTB: sums.revenueOTB,
          revenueBudget: sums.revenueBudget,
        },
      ],
    };
  });

  return result;
};

// Add grand total row at end (sum of all segments for each date column)
// Use data[0] to determine # of columns, then data.reduce() to sum all segments
const addVerticalTotal = (data) => {
  if (!data.length || !data[0].budgetModel.length) {
    return data;
  }

  const columnSums = data[0].budgetModel.map((_, colIndex) => {
    return data.reduce(
      (acc, row) => {
        const model = row.budgetModel[colIndex];
        if (!model) return acc;

        const isTotal = model.segmentID === 0;
        return {
          roomsActual:
            acc.roomsActual +
            (isTotal ? 0 : model.roomsActual === -1 ? 0 : model.roomsActual),
          roomsLocked:
            acc.roomsLocked +
            (isTotal ? 0 : model.roomsLocked === -1 ? 0 : model.roomsLocked),
          roomsLY:
            acc.roomsLY +
            (isTotal ? 0 : model.roomsLY === -1 ? 0 : model.roomsLY),
          roomsOTB:
            acc.roomsOTB +
            (isTotal ? 0 : model.roomsOTB === -1 ? 0 : model.roomsOTB),
          roomsBudget:
            acc.roomsBudget +
            (isTotal ? 0 : model.roomsBudget === -1 ? 0 : model.roomsBudget),
          revenueActual:
            acc.revenueActual +
            (isTotal
              ? 0
              : model.revenueActual === -1
              ? 0
              : model.revenueActual),
          revenueLocked:
            acc.revenueLocked +
            (isTotal
              ? 0
              : model.revenueLocked === -1
              ? 0
              : model.revenueLocked),
          revenueLY:
            acc.revenueLY +
            (isTotal ? 0 : model.revenueLY === -1 ? 0 : model.revenueLY),
          revenueOTB:
            acc.revenueOTB +
            (isTotal ? 0 : model.revenueOTB === -1 ? 0 : model.revenueOTB),
          revenueBudget:
            acc.revenueBudget +
            (isTotal
              ? 0
              : model.revenueBudget === -1
              ? 0
              : model.revenueBudget),
        };
      },
      {
        roomsActual: 0,
        roomsLocked: 0,
        roomsLY: 0,
        roomsOTB: 0,
        roomsBudget: 0,
        revenueActual: 0,
        revenueLocked: 0,
        revenueLY: 0,
        revenueOTB: 0,
        revenueBudget: 0,
      },
    );
  });

  const budgetModel = columnSums.map((sums) => ({
    segmentID: 0,
    date: new Date(),
    roomsActual: sums.roomsActual,
    roomsLocked: sums.roomsLocked,
    roomsLY: sums.roomsLY,
    roomsOTB: sums.roomsOTB,
    roomsBudget: sums.roomsBudget,
    arrActual: calculateADR(sums.roomsActual, sums.revenueActual),
    arrLocked: calculateADR(sums.roomsLocked, sums.revenueLocked),
    arrLY: calculateADR(sums.roomsLY, sums.revenueLY),
    arrOTB: calculateADR(sums.roomsOTB, sums.revenueOTB),
    arrBudget: calculateADR(sums.roomsBudget, sums.revenueBudget),
    revenueActual: sums.revenueActual,
    revenueLocked: sums.revenueLocked,
    revenueLY: sums.revenueLY,
    revenueOTB: sums.revenueOTB,
    revenueBudget: sums.revenueBudget,
  }));

  data.push({
    segmentID: 0,
    systemName: "Total",
    budgetModel,
  });

  return data;
};


// Helper function to recalculate vertical totals for the entire forecast sheet.
// This function should be defined outside the useForecastSheet hook for reusability.
const recalculateVerticalTotals = (currentData) => {
  const totalRowIndex = currentData.length - 1;
  // If there's no total row or data is empty, return as is.
  if (totalRowIndex < 0 || !currentData[totalRowIndex] || !currentData[totalRowIndex].budgetModel) {
    return currentData;
  }

  const totalRow = currentData[totalRowIndex];

  // For each date column, sum up all segments *except* the overall Total row itself.
  const updatedTotalBudgetModel = totalRow.budgetModel.map(
    (model, colIndex) => {
      const columnSums = currentData.slice(0, -1).reduce(
        (acc, segment) => {
          const segModel = segment.budgetModel?.[colIndex];
          if (!segModel) return acc;

          // Helper to safely get value, treating -1, NaN, null, undefined as 0.
          const getSafeValue = (field) =>
            segModel[field] === -1 || isNaN(segModel[field]) ? 0 : segModel[field] || 0;

          return {
            roomsActual: acc.roomsActual + getSafeValue('roomsActual'),
            roomsLocked: acc.roomsLocked + getSafeValue('roomsLocked'),
            roomsLY: acc.roomsLY + getSafeValue('roomsLY'),
            roomsOTB: acc.roomsOTB + getSafeValue('roomsOTB'),
            roomsBudget: acc.roomsBudget + getSafeValue('roomsBudget'),
            revenueActual: acc.revenueActual + getSafeValue('revenueActual'),
            revenueLocked: acc.revenueLocked + getSafeValue('revenueLocked'),
            revenueLY: acc.revenueLY + getSafeValue('revenueLY'),
            revenueOTB: acc.revenueOTB + getSafeValue('revenueOTB'),
            revenueBudget: acc.revenueBudget + getSafeValue('revenueBudget'),
          };
        },
        { // Initial accumulator for column sums, covering all relevant fields.
          roomsActual: 0, roomsLocked: 0, roomsLY: 0, roomsOTB: 0, roomsBudget: 0,
          revenueActual: 0, revenueLocked: 0, revenueLY: 0, revenueOTB: 0, revenueBudget: 0,
        },
      );

      // Update the total row's model for this column with the calculated sums and derived ADRs.
      return {
        ...model,
        roomsActual: columnSums.roomsActual,
        roomsLocked: columnSums.roomsLocked,
        roomsLY: columnSums.roomsLY,
        roomsOTB: columnSums.roomsOTB,
        roomsBudget: columnSums.roomsBudget,
        revenueActual: columnSums.revenueActual,
        revenueLocked: columnSums.revenueLocked,
        revenueLY: columnSums.revenueLY,
        revenueOTB: columnSums.revenueOTB,
        revenueBudget: columnSums.revenueBudget,
        arrActual: safeDivide(columnSums.revenueActual, columnSums.roomsActual),
        arrLocked: safeDivide(columnSums.revenueLocked, columnSums.roomsLocked),
        arrLY: safeDivide(columnSums.revenueLY, columnSums.roomsLY),
        arrOTB: safeDivide(columnSums.revenueOTB, columnSums.roomsOTB),
        arrBudget: safeDivide(columnSums.revenueBudget, columnSums.roomsBudget),
      };
    },
  );

  const finalData = [...currentData];
  finalData[totalRowIndex] = {
    ...totalRow,
    budgetModel: updatedTotalBudgetModel,
  };
  return finalData;
};
export default function useForecastSheet() {
  const date = new Date();
  const [forecastSheetData, setForecastSheetData] = useState(null);
  const { token, authFetch, currentHotel } = useAuth();
  const { hotelId } = useParams();
  const [comparitiveDate, setComparitiveDate] = useState(
    new Date(date.getFullYear(), date.getMonth(), 1),
  );
  const [showPickupRequireToAchieve, setShowPickupRequireToAchieve] = useState(
    null,
  );
  const [selectedPickup, setSelectedPickup] = useState([0, 0, 0]);
  const [filterDialogBox, setFilterDialoagBox] = useState(false);

  const [networkMsg, setnetworkMsg] = useState();
  const [open, setOpen] = useState(null);
  const [Loading, setLoading] = useState(false);
  const [competitorData, setCompetitorsData] = useState(null);
  const [dates, setDates] = useState(null);
  const [filterArray, setFilterArray] = useState([
    true,
    true,
    true,
    true,
    true,
  ]);

  const [filterAll, setFilterAll] = useState(true);

  const handleFilterDialogBox = useCallback(() => {
    setFilterDialoagBox(!filterDialogBox);
  }, [filterDialogBox]);

  const handleFilter = useCallback((value, tag) => {
    const filterMap = {
      "Last Year": 0,
      Budget: 1,
      "Locked Forecast": 2,
      BOB: 3,
      Actual: 4,
      All: -1,
    };

    const filterIndex = filterMap[tag];

    if (tag === "All") {
      setFilterArray([false, false, false, false, false]);
      setFilterAll(true);
    } else if (filterIndex !== undefined && filterIndex >= 0) {
      setFilterArray((prev) => {
        const updated = [...prev];
        updated[filterIndex] = value;
        return updated;
      });
      setFilterAll(false);
    }
  }, []);

  useEffect(() => {
    setFilterAll(filterArray?.every((value) => value === false));
  }, [filterArray]);

  const getForecastData = useCallback(async () => {
    setLoading(true);
    try {
      const { get } = await authFetch({
        path: `/hotel/${hotelId}/budget-forecast-daily/${getISODate(
          new Date(
            comparitiveDate.getFullYear(),
            comparitiveDate.getMonth(),
            1,
          ),
        )}`,
      });

      const { data } = await get();

      if (data && data?.length) {
        const withHorizontalTotal = addHorizontalTotal(data);

        const withVerticalTotal = addVerticalTotal(withHorizontalTotal);

        setShowPickupRequireToAchieve(
          new Array(withVerticalTotal.length + 1).fill(false),
        );
        const transformedData = transformData(
          withVerticalTotal,
          checkIfMinusOne,
        );

        setForecastSheetData(transformedData);

        const defaultPickups = [1, 1, 1];
        const pickupsArray = new Array(withVerticalTotal.length).fill(
          defaultPickups,
        );
        setSelectedPickup(pickupsArray);
      } else {
        setOpen("error");
        setnetworkMsg("No Forecast Data");
        setShowPickupRequireToAchieve(null);
        setForecastSheetData(null);
      }
    } catch (error) {
      setOpen("error");
      setnetworkMsg("Error fetching data");
    } finally {
      setLoading(false);
    }
  }, [comparitiveDate, hotelId, authFetch]);

  const postForecastData = useCallback(async () => {
    try {
      const { post } = await authFetch({
        path: `/hotel/${hotelId}/post-budget-forecast-daily/${getISODate(
          new Date(
            comparitiveDate.getFullYear(),
            comparitiveDate.getMonth(),
            1,
          ),
        )}`,
      });

      // Create shallow copy and remove total row
      const data1 = forecastSheetData.slice();
      data1.pop();

      // Filter out horizontal totals
      const container = data1.map((segData) => ({
        ...segData,
        budgetModel: segData.budgetModel.filter((d) => d.segmentID !== 0),
      }));

      const dataToSend = transformData(container, convertToMinusOne);
      const { response } = await post(dataToSend);

      if (response?.ok) {
        setOpen("success");
        setnetworkMsg("Data saved successfully");
        await getForecastData();
      } else {
        setnetworkMsg("Failed to save data");
        setOpen("error");
      }
    } catch (error) {
      setnetworkMsg("Error saving data");
      setOpen("error");
    }
  }, [forecastSheetData, hotelId, comparitiveDate, authFetch, getForecastData]);

  // const fetchInnkeyRevenueDetails = useCallback(async () => {
  //   setLoading(true);
  //   try {
  //     const { get } = await authFetch({
  //       path: `/hotel/${hotelId}/innkey-revenue-details`,
  //     });

  //     const { data, response } = await get();

  //     if (response?.ok) {
  //       getForecastData();
  //       setnetworkMsg("Data fetched successfully");
  //       setOpen("success");
  //     } else {
  //       setnetworkMsg(data?.messageToUser || "No Forecast Data");
  //       setOpen("error");
  //     }
  //   } catch (error) {
  //     console.log({ error });
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [getForecastData, hotelId, authFetch]);

  const getCompetitorsData = useCallback(async () => {
    try {
      const { get } = await authFetch({
        path: `/hotel/${hotelId}/competitors-monthly-report/${getISODate(
          new Date(
            comparitiveDate.getFullYear(),
            comparitiveDate.getMonth(),
            1,
          ),
        )}`,
      });
      const { data } = await get();
      if (data && Object.keys(data).length) {
        setCompetitorsData(data);
      } else {
        setCompetitorsData(null);
      }
    } catch (e) {
      // Error handled silently
      console.error("Error fetching competitors data on forecast screen:", e);
    }
  }, [comparitiveDate, authFetch, hotelId]);

  function generateDate(date) {
    const DateArray = [];
    var firstDay = 1;
    var lastDay = new Date(
      date.getFullYear(),
      date.getMonth() + 1,
      0,
    ).getDate();
    for (firstDay = 1; firstDay <= lastDay; firstDay++) {
      DateArray.push(new Date(date.getFullYear(), date.getMonth(), firstDay));
    }
    setDates(DateArray);
  }

  function checkIfMinusOne(num) {
    if (parseInt(num) === -1) {
      return NaN;
    }
    return num;
  }

  function convertToMinusOne(num) {
    if (isNaN(num)) {
      return -1;
    }
    return num;
  }

const handleActualChange = useDebouncedCallback(
  (index, segmentIndex, actual, value) => {
    setForecastSheetData((prevData) => {
      // Ensure prevData and the target segment exist before proceeding.
      if (!prevData || !prevData[segmentIndex]) return prevData;
      // Step 1: Update the specific day's value and recalculate derived fields within the target segment.
      const dataWithUpdatedSegment = prevData.map((fSD, fSIndex) => {
        if (fSIndex === segmentIndex) {
          const updatedBudgetModelForSegment = fSD.budgetModel.map((bM, bMIndex) => {
            if (bMIndex === index) {
              let updatedDayModel = { ...bM, [actual]: value };
              
              // Convert NaN to 0 for calculations
              const rooms = isNaN(updatedDayModel.roomsActual) ? 0 : updatedDayModel.roomsActual;
              const arr = isNaN(updatedDayModel.arrActual) ? 0 : updatedDayModel.arrActual;
              const revenue = isNaN(updatedDayModel.revenueActual) ? 0 : updatedDayModel.revenueActual;
              
              if (actual === 'roomsActual' || actual === 'arrActual') {
                updatedDayModel.revenueActual = rooms * arr;
              }
              else if (actual === 'revenueActual') {
                updatedDayModel.arrActual = safeDivide(revenue, rooms);
              }
              return updatedDayModel;
            }
            return bM;
          });
          // Step 2: Recalculate the horizontal total for this specific segment.
          // This involves summing up all daily models within the segment (excluding its own total row if present).
          const dailyModelsForHorizontalSum = updatedBudgetModelForSegment.filter(model => model.segmentID !== 0);
          const sumsForSegment = calculateBudgetSums(dailyModelsForHorizontalSum);
          const newHorizontalTotalModel = {
            segmentID: 0, // Conventionally, segmentID 0 represents the horizontal total for a segment.
            // Populate all fields for the horizontal total model based on sums and derived ADRs.
            roomsActual: sumsForSegment.roomsActual,
            roomsLocked: sumsForSegment.roomsLocked,
            roomsLY: sumsForSegment.roomsLY,
            roomsOTB: sumsForSegment.roomsOTB,
            roomsBudget: sumsForSegment.roomsBudget,
            revenueActual: sumsForSegment.revenueActual,
            revenueLocked: sumsForSegment.revenueLocked,
            revenueLY: sumsForSegment.revenueLY,
            revenueOTB: sumsForSegment.revenueOTB,
            revenueBudget: sumsForSegment.revenueBudget,
            arrActual: calculateADR(sumsForSegment.roomsActual, sumsForSegment.revenueActual),
            arrLocked: calculateADR(sumsForSegment.revenueLocked, sumsForSegment.roomsLocked),
            arrLY: calculateADR(sumsForSegment.revenueLY, sumsForSegment.roomsLY),
            arrOTB: calculateADR(sumsForSegment.revenueOTB, sumsForSegment.roomsOTB),
            arrBudget: calculateADR(sumsForSegment.revenueBudget, sumsForSegment.roomsBudget),
          };
          // Replace the old horizontal total model with the new one, or add it if not present.
          const finalBudgetModelForSegment = updatedBudgetModelForSegment.map(model =>
            model.segmentID === 0 ? newHorizontalTotalModel : model
          );
          // If the total model was not found (e.g., first time calculation), add it at the end.
          if (!finalBudgetModelForSegment.some(model => model.segmentID === 0)) {
            finalBudgetModelForSegment.push(newHorizontalTotalModel);
          }
          return {
            ...fSD,
            budgetModel: finalBudgetModelForSegment,
          };
        }
        return fSD;
      });
      // Step 3: Recalculate the overall vertical total (the last row in the entire forecast sheet data).
      const finalUpdatedData = recalculateVerticalTotals(dataWithUpdatedSegment);
      return finalUpdatedData;
    });
  },
  100,
);

const handleRevenueChange = useDebouncedCallback(
  (index, segmentIndex, tag, value) => {
    setForecastSheetData((prevData) => {
      // Ensure prevData and the target segment exist before proceeding.
      if (!prevData || !prevData[segmentIndex]) return prevData;

      // Step 1: Update the specific day's value and recalculate derived fields within the target segment.
      const dataWithUpdatedSegment = prevData.map((fSD, fSIndex) => {
        if (fSIndex === segmentIndex) {
          // This is the segment being modified.
          const updatedBudgetModelForSegment = fSD.budgetModel.map((bM, bMIndex) => {
            if (bMIndex === index) {
              let updatedDayModel = { ...bM, [tag]: value };

              const roomsBudget = isNaN(updatedDayModel.roomsBudget) ? 0 : updatedDayModel.roomsBudget;
              const arrBudget = isNaN(updatedDayModel.arrBudget) ? 0 : updatedDayModel.arrBudget;
              const revenueBudget = isNaN(updatedDayModel.revenueBudget) ? 0 : updatedDayModel.revenueBudget;

              // Recalculate revenueBudget or arrBudget based on the changed field.
              if (tag === 'roomsBudget') {
                updatedDayModel.revenueBudget = roomsBudget * arrBudget;
                updatedDayModel.arrBudget = safeDivide(revenueBudget, roomsBudget);
              } else if (tag === 'arrBudget') {
                updatedDayModel.revenueBudget = roomsBudget * arrBudget;
              } else if (tag === 'revenueBudget') {
                updatedDayModel.arrBudget = safeDivide(revenueBudget, roomsBudget);
              }
              return updatedDayModel;
            }
            return bM;
          });

          // Step 2: Recalculate the horizontal total for this specific segment.
          // This involves summing up all daily models within the segment (excluding its own total row if present).
          const dailyModelsForHorizontalSum = updatedBudgetModelForSegment.filter(model => model.segmentID !== 0);
          const sumsForSegment = calculateBudgetSums(dailyModelsForHorizontalSum);

          const newHorizontalTotalModel = {
            segmentID: 0, // Conventionally, segmentID 0 represents the horizontal total for a segment.
            // Populate all fields for the horizontal total model based on sums and derived ADRs.
            roomsActual: sumsForSegment.roomsActual,
            roomsLocked: sumsForSegment.roomsLocked,
            roomsLY: sumsForSegment.roomsLY,
            roomsOTB: sumsForSegment.roomsOTB,
            roomsBudget: sumsForSegment.roomsBudget,
            revenueActual: sumsForSegment.revenueActual,
            revenueLocked: sumsForSegment.revenueLocked,
            revenueLY: sumsForSegment.revenueLY,
            revenueOTB: sumsForSegment.revenueOTB,
            revenueBudget: sumsForSegment.revenueBudget,
            arrActual: calculateADR(sumsForSegment.roomsActual, sumsForSegment.revenueActual),
            arrLocked: calculateADR(sumsForSegment.roomsLocked, sumsForSegment.roomsLocked),
            arrLY: calculateADR(sumsForSegment.revenueLY, sumsForSegment.roomsLY),
            arrOTB: calculateADR(sumsForSegment.revenueOTB, sumsForSegment.roomsOTB),
            arrBudget: calculateADR(sumsForSegment.revenueBudget, sumsForSegment.roomsBudget), 
          };

          // Replace the old horizontal total model with the new one, or add it if not present.
          const finalBudgetModelForSegment = updatedBudgetModelForSegment.map(model =>
            model.segmentID === 0 ? newHorizontalTotalModel : model
          );
          // If the total model was not found (e.g., first time calculation), add it at the end.
          if (!finalBudgetModelForSegment.some(model => model.segmentID === 0)) {
            finalBudgetModelForSegment.push(newHorizontalTotalModel);
          }

          return {
            ...fSD,
            budgetModel: finalBudgetModelForSegment,
          };
        }
        return fSD;
      });

      // Step 3: Recalculate the overall vertical total (the last row in the entire forecast sheet data).
      const finalUpdatedData = recalculateVerticalTotals(dataWithUpdatedSegment);
      return finalUpdatedData;
    });
  },
  100,
);

  const handleShowPickupRequiredRow = useCallback(
    (index) => {
      setShowPickupRequireToAchieve(
        showPickupRequireToAchieve.map((item, idx) => {
          if (idx === index) {
            return !item;
          }
          return item;
        }),
      );
    },
    [showPickupRequireToAchieve],
  );

  const setPickup = useCallback((segmentIndex, pickupIndex, value) => {
    setSelectedPickup((prev) =>
      prev.map((pickups, idx) => {
        if (idx !== segmentIndex) return pickups;
        return pickups.map((p, pIdx) => (pIdx === pickupIndex ? value : p));
      }),
    );
  }, []);

  useEffect(() => {
    generateDate(comparitiveDate);
    getForecastData();
    getCompetitorsData();
  }, [comparitiveDate, token, hotelId, getCompetitorsData, getForecastData]);
  return {
    forecastSheetData,
    comparitiveDate,
    setComparitiveDate,
    handleActualChange,
    handleRevenueChange,
    showPickupRequireToAchieve,
    handleShowPickupRequiredRow,
    postForecastData,
    setSelectedPickup,
    selectedPickup,
    Loading,
    setPickup,
    competitorData,
    dates,
    setOpen,
    networkMsg,
    open,
    handleFilterDialogBox,
    filterDialogBox,
    handleFilter,
    // fetchInnkeyRevenueDetails,
    filterArray,
    filterAll,
    currentHotel,
  };
}
